import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const PALAVRA_CONFIRMACAO = 'CONFIRMO';
const JUSTIFICATIVA_MINIMA = 10;

export interface VinculosEmpresa {
  usuarios: number;
  contatos: number;
  tickets: number;
  mensagens: number;
  instancias: number;
  filas: number;
  agendamentos: number;
}

export interface DadosExclusaoEmpresa {
  empresa: { id: string; nome: string };
  vinculos: VinculosEmpresa;
}

interface Props {
  dados: DadosExclusaoEmpresa | null;
  onClose: () => void;
  onExcluida: () => void;
}

const ROTULOS: { chave: keyof VinculosEmpresa; label: string }[] = [
  { chave: 'usuarios', label: 'Usuários' },
  { chave: 'contatos', label: 'Contatos' },
  { chave: 'tickets', label: 'Tickets' },
  { chave: 'mensagens', label: 'Mensagens' },
  { chave: 'instancias', label: 'Instâncias (conexões WhatsApp)' },
  { chave: 'filas', label: 'Filas' },
  { chave: 'agendamentos', label: 'Mensagens agendadas' },
];

const extractError = (e: any) => e?.response?.data?.message || e?.message || 'Erro inesperado';

const ExcluirEmpresaModal: React.FC<Props> = ({ dados, onClose, onExcluida }) => {
  const { toast } = useToast();
  const [justificativa, setJustificativa] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pararPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = null;
  };

  // Limpa o formulário sempre que abrir para outra empresa
  useEffect(() => {
    setJustificativa('');
    setConfirmacao('');
    setErro(null);
    setExcluindo(false);
    pararPolling();
  }, [dados?.empresa.id]);

  useEffect(() => pararPolling, []);

  const valido = confirmacao.trim() === PALAVRA_CONFIRMACAO && justificativa.trim().length >= JUSTIFICATIVA_MINIMA;

  const acompanhar = (logId: string) => {
    pararPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/empresas/exclusoes/${logId}`);
        if (data.log.status === 'SUCESSO') {
          pararPolling();
          setExcluindo(false);
          toast({ title: 'Empresa excluída', description: `${dados?.empresa.nome} e todos os seus dados foram removidos.` });
          onExcluida();
        } else if (data.log.status === 'ERRO') {
          pararPolling();
          setExcluindo(false);
          setErro(`A exclusão falhou e nada foi removido do banco: ${data.log.erro || 'erro desconhecido'}`);
        }
      } catch {
        // tenta de novo no próximo ciclo
      }
    }, 2000);
  };

  const handleExcluir = async () => {
    if (!dados || !valido) return;
    setErro(null);
    setExcluindo(true);
    try {
      const { data } = await api.post(`/empresas/${dados.empresa.id}/exclusao-completa`, {
        confirmacao: confirmacao.trim(),
        justificativa: justificativa.trim(),
      });
      acompanhar(data.log.id);
    } catch (e) {
      setExcluindo(false);
      setErro(extractError(e));
    }
  };

  return (
    <Dialog open={!!dados} onOpenChange={(open) => { if (!open && !excluindo) onClose(); }}>
      <DialogContent
        className="sm:max-w-lg dark:bg-slate-800"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => { if (excluindo) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" /> Excluir empresa e todos os dados
          </DialogTitle>
          <DialogDescription className="dark:text-gray-300">
            A empresa <strong>{dados?.empresa.nome}</strong> possui registros vinculados. A exclusão completa remove
            permanentemente:
          </DialogDescription>
        </DialogHeader>

        {dados && (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm dark:text-white">
            {ROTULOS.filter((r) => dados.vinculos[r.chave] > 0).map((r) => (
              <li key={r.chave} className="flex justify-between gap-2">
                <span>{r.label}</span>
                <strong>{dados.vinculos[r.chave].toLocaleString('pt-BR')}</strong>
              </li>
            ))}
            <li className="col-span-2">Todas as mídias (imagens, vídeos, áudios e documentos)</li>
          </ul>
        )}

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Conexões Uazapi e Evolution também são removidas no provedor. Não é possível desfazer. A exclusão fica registrada
          com o seu usuário, a data e a justificativa.
        </p>

        <div className="space-y-3">
          <div>
            <Label htmlFor="justificativaExclusao" className="dark:text-white">Justificativa</Label>
            <Textarea
              id="justificativaExclusao"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Por que esta empresa está sendo excluída?"
              disabled={excluindo}
              className="mt-1 dark:bg-slate-700 dark:text-white"
              rows={3}
            />
            {justificativa.trim().length > 0 && justificativa.trim().length < JUSTIFICATIVA_MINIMA && (
              <p className="text-xs text-amber-600 mt-1">Mínimo de {JUSTIFICATIVA_MINIMA} caracteres.</p>
            )}
          </div>
          <div>
            <Label htmlFor="confirmacaoExclusao" className="dark:text-white">
              Digite <strong>{PALAVRA_CONFIRMACAO}</strong> para confirmar
            </Label>
            <Input
              id="confirmacaoExclusao"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              placeholder={PALAVRA_CONFIRMACAO}
              autoComplete="off"
              disabled={excluindo}
              className="mt-1 dark:bg-slate-700 dark:text-white"
            />
          </div>
        </div>

        {excluindo && (
          <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" /> Excluindo... em empresas grandes isso pode levar alguns minutos.
          </p>
        )}
        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={excluindo} className="dark:text-white">
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleExcluir} disabled={!valido || excluindo}>
            {excluindo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Excluir definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExcluirEmpresaModal;
